import React, { useState, useEffect } from 'react';
import { Loader2, SplitSquareHorizontal, Plus, X } from 'lucide-react';
import { getInfo, splitColumn } from '../../utils/api';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import MissingChoice from './MissingChoice';

const SplitColumnOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  
  const [config, setConfig] = useState({
    column: '',
    delimiter: '',
    new_column_names: [''],
    expand: true
  });

  useEffect(() => {
    loadColumns();
  }, []);

  const loadColumns = async () => {
    try {
      const result = await getInfo();
      setColumns(result.info.column_names || []);
    } catch (error) {
      toast.error(t('form.loadColumnsFailed'));
    } finally {
      setLoading(false);
    }
  };

  const addNewColumnName = () => {
    setConfig(prev => ({
      ...prev,
      new_column_names: [...prev.new_column_names, '']
    }));
  };

  const removeColumnName = (index) => {
    if (config.new_column_names.length > 1) {
      setConfig(prev => ({
        ...prev,
        new_column_names: prev.new_column_names.filter((_, i) => i !== index)
      }));
    }
  };

  const updateColumnName = (index, value) => {
    setConfig(prev => ({
      ...prev,
      new_column_names: prev.new_column_names.map((name, i) => i === index ? value : name)
    }));
  };

  const handleApply = async () => {
    if (!config.column) {
      toast.error(t('split.needColumn'));
      return;
    }
    if (!config.delimiter) {
      toast.error(t('split.needDelimiter'));
      return;
    }
    const validNames = config.new_column_names.filter(n => n.trim());
    if (validNames.length === 0) {
      toast.error(t('split.needName'));
      return;
    }

    try {
      setApplying(true);
      const result = await splitColumn({
        column: config.column,
        delimiter: config.delimiter,
        new_column_names: validNames,
        expand: config.expand
      });
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('split.failed'));
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 text-muted-foreground">
        <SplitSquareHorizontal className="h-5 w-5" />
        <p className="text-sm">Split a column into multiple columns based on a delimiter</p>
      </div>

      {/* Column to split */}
      <div>
        <label className="label mb-2 block">{t('split.column')}</label>
        <select
          value={config.column}
          onChange={(e) => setConfig(prev => ({ ...prev, column: e.target.value }))}
          className="select"
          data-testid="split-column-select"
        >
          <option value="">{t('email.selectColumn')}</option>
          {columns.map(col => (
            <option key={col} value={col}>{col}</option>
          ))}
        </select>
      </div>

      {/* Delimiter */}
      <div>
        <label className="label mb-2 block">{t('split.delimiter')}</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={config.delimiter}
            onChange={(e) => setConfig(prev => ({ ...prev, delimiter: e.target.value }))}
            placeholder={t('split.delimiterPlaceholder')}
            className="input flex-1"
            data-testid="split-delimiter"
          />
          <div className="flex gap-1">
            {[',', ';', '-', ' ', '_', '/'].map(d => (
              <button
                key={d}
                type="button"
                onClick={() => setConfig(prev => ({ ...prev, delimiter: d }))}
                className={`px-3 py-2 rounded-lg text-sm font-mono border transition-colors ${
                  config.delimiter === d
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted border-border hover:border-primary'
                }`}
              >
                {d === ' ' ? '␣' : d}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* New column names */}
      <div>
        <label className="label mb-2 block">{t('split.namesLabel')}</label>
        <p className="text-xs text-muted-foreground mb-2">{t('split.namesHelp')}</p>
        <div className="space-y-2">
          {config.new_column_names.map((name, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground w-8">#{index + 1}</span>
              <input
                type="text"
                value={name}
                onChange={(e) => updateColumnName(index, e.target.value)}
                placeholder={t('split.namePlaceholder', { index: index + 1 })}
                className="input flex-1"
                data-testid={`split-new-col-${index}`}
              />
              {config.new_column_names.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeColumnName(index)}
                  className="btn-ghost h-10 w-10 p-0 text-destructive"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addNewColumnName}
            className="btn-secondary flex items-center gap-2 w-full justify-center"
            data-testid="split-add-column"
          >
            <Plus className="h-4 w-4" />
            {t('split.addColumn')}
          </button>
        </div>
      </div>

      {/* Preview */}
      {config.column && config.delimiter && (
        <div className="p-4 bg-muted/50 rounded-lg">
          <p className="text-sm text-muted-foreground">
            <strong>{t('split.previewLabel')}</strong>{' '}
            {t('split.previewBody', {
              column: config.column,
              delimiter: config.delimiter,
              count: config.new_column_names.filter(n => n.trim()).length || 1,
            })}
          </p>
        </div>
      )}

      {/* Actions */}
      <MissingChoice reasons={[!config.column && t('split.needColumn'), !config.delimiter && t('split.needDelimiter')]} />

      <div className="flex justify-end gap-3 pt-4 border-t border-border">
        <button onClick={onCancel} className="btn-secondary" data-testid="split-cancel">
          {t('form.cancel')}
        </button>
        <button
          onClick={handleApply}
          disabled={applying || !config.column || !config.delimiter}
          className="btn-primary flex items-center gap-2"
          data-testid="split-apply"
        >
          {applying && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('split.apply')}
        </button>
      </div>
    </div>
  );
};

export default SplitColumnOperation;
